/**
 * Sidebar Settings
 *
 * @module shared/components/navigation/sidebarSettings
 *
 * Sidebar dimensions and navigation menu items configuration
 */

import ListAltIcon from '@mui/icons-material/ListAlt';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentsIcon from '@mui/icons-material/Payments';
import ContactsIcon from '@mui/icons-material/Contacts';
import PlaceIcon from '@mui/icons-material/Place';
import QueueIcon from '@mui/icons-material/Queue';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import HistoryIcon from '@mui/icons-material/History';
import PhoneIcon from '@mui/icons-material/Phone';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { createElement } from 'react';

import { APP_ROUTES } from '@/shared/constants';
import type { NavGroup, NavItem } from './types';

// ============================================================================
// Sidebar Dimensions
// ============================================================================

/**
 * Sidebar width when expanded (in pixels)
 */
export const SIDEBAR_WIDTH = 300;

/**
 * Sidebar width when collapsed (in pixels)
 */
export const SIDEBAR_COLLAPSED_WIDTH = 60;

// ============================================================================
// Navigation Items
// ============================================================================

/**
 * Navigation items grouped into domain clusters.
 *
 * Each group renders as a labelled section in the sidebar, in order.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Telephony & Queues',
    items: [
      { label: 'Dashboard', path: APP_ROUTES.HOME, icon: createElement(DashboardIcon, { fontSize: 'small' }) },
      { label: 'Queue', path: APP_ROUTES.QUEUE, icon: createElement(QueueIcon, { fontSize: 'small' }) },
      { label: 'Call Follow-Ups', path: APP_ROUTES.CALL_FOLLOW_UPS, icon: createElement(EventRepeatIcon, { fontSize: 'small' }) },
      { label: 'Call History', path: APP_ROUTES.CALL_HISTORY, icon: createElement(HistoryIcon, { fontSize: 'small' }) },
      { label: 'Phone search', path: APP_ROUTES.PHONE_SEARCH, icon: createElement(PhoneIcon, { fontSize: 'small' }) },
    ],
  },
  {
    label: 'Case Lifecycle',
    items: [
      { label: 'Cases', path: APP_ROUTES.CASES.LIST, icon: createElement(ListAltIcon, { fontSize: 'small' }) },
      { label: 'PTP', path: APP_ROUTES.PTP, icon: createElement(CheckCircleIcon, { fontSize: 'small' }) },
      { label: 'Payments', path: APP_ROUTES.PAYMENTS, icon: createElement(PaymentsIcon, { fontSize: 'small' }) },
      { label: 'Deferments', path: APP_ROUTES.DEFERMENTS, icon: createElement(ScheduleIcon, { fontSize: 'small' }) },
      { label: 'Case Tags', path: APP_ROUTES.CASE_TAGS, icon: createElement(LocalOfferIcon, { fontSize: 'small' }) },
    ],
  },
  {
    label: 'Operations & System',
    items: [
      { label: 'Clients', path: APP_ROUTES.CLIENTS.LIST, icon: createElement(ContactsIcon, { fontSize: 'small' }) },
      { label: 'Entities', path: APP_ROUTES.ENTITIES, icon: createElement(BusinessIcon, { fontSize: 'small' }) },
      { label: 'Field Visits', path: APP_ROUTES.FIELD_VISITS, icon: createElement(PlaceIcon, { fontSize: 'small' }) },
      { label: 'Users', path: APP_ROUTES.USERS.LIST, icon: createElement(PeopleIcon, { fontSize: 'small' }) },
      { label: 'OAuth Clients', path: APP_ROUTES.OAUTH_CLIENTS.LIST, icon: createElement(VpnKeyIcon, { fontSize: 'small' }), requiresOAuthClientManagement: true },
    ],
  },
];

/**
 * Flattened navigation items - kept for backwards compatibility with
 * consumers that expect a single list of every navigable destination.
 */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap(group => group.items);

/**
 * Secondary navigation items - retained for backwards compatibility.
 * All items are now surfaced directly within their domain group.
 */
export const MORE_NAV_ITEMS: NavItem[] = [];
